import React, { useId, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { FaFileWord } from "react-icons/fa";
import { FiLoader } from "react-icons/fi";

const stageMessages = {
  queued: "已排入佇列，等待開始匯出…",
  loading: "正在讀取題目內容…",
  preparing: "正在準備題目圖文與公式…",
  rendering: "正在排版題目…",
  finalizing: "正在整理文件版面…",
  packaging: "正在打包 Word 文件…",
  downloading: "正在下載 Word 文件…",
  completed: "Word 文件已準備完成…",
};
const questionStages = new Set(["loading", "preparing", "rendering"]);

export default function WordExportProgressModal({ filename, message, progress, onCancel, cancelling = false }) {
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  const descriptionId = useId();
  const cancelButtonRef = useRef(null);
  const cancelRequested = useRef(false);
  const controlsRef = useRef(null);
  controlsRef.current = { onCancel, cancelling };
  const requestCancel = () => {
    const controls = controlsRef.current;
    if (!controls.onCancel || controls.cancelling || cancelRequested.current) return;
    cancelRequested.current = true;
    controls.onCancel();
  };
  const total = Number(progress?.total);
  const current = Number(progress?.current);
  const showCount = !cancelling && questionStages.has(progress?.stage) && total > 0 &&
    Number.isFinite(total) && progress?.current != null && Number.isFinite(current) && current >= 0 && current <= total;
  const statusMessage = cancelling ? "正在取消匯出，等待作業停止…"
    : progress?.message || message || stageMessages[progress?.stage] || "正在整理題目與版面，請稍候…";
  const displayFilename = filename || progress?.filename;

  useLayoutEffect(() => {
    const previousFocus = document.activeElement;
    const overflow = document.body.style.getPropertyValue("overflow");
    const overflowPriority = document.body.style.getPropertyPriority("overflow");
    const background = Array.from(document.body.children)
      .filter((element) => element !== overlayRef.current)
      .map((element) => [element, element.getAttribute("inert")]);
    background.forEach(([element]) => element.setAttribute("inert", ""));
    document.body.style.setProperty("overflow", "hidden");

    // Capture before the editor/reader shortcuts, including window listeners.
    const blockKeyboard = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.type !== "keydown") return;
      if (event.key === "Tab") {
        const button = cancelButtonRef.current;
        (button && !button.disabled ? button : dialogRef.current)?.focus({ preventScroll: true });
      } else if (event.key === "Escape" || ((event.key === "Enter" || event.key === " ") && event.target === cancelButtonRef.current)) {
        const controls = controlsRef.current;
        if (controls.onCancel && !controls.cancelling && !cancelRequested.current) {
          cancelRequested.current = true;
          controls.onCancel();
        }
      }
    };
    const keepFocus = (event) => {
      if (!dialogRef.current?.contains(event.target)) {
        event.stopImmediatePropagation();
        dialogRef.current?.focus({ preventScroll: true });
      }
    };
    const blockBackgroundPointer = (event) => {
      // The export finishes by clicking its generated Blob download link while
      // this progress overlay is still mounted. Let that download proceed.
      const downloadLink = event.target?.closest?.("a[download]");
      if (event.type === "click" && event.isTrusted === false && downloadLink?.href.startsWith("blob:")) return;
      if (!dialogRef.current?.contains(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    const keyboardEvents = ["keydown", "keypress", "keyup"];
    const pointerEvents = ["pointerdown", "mousedown", "touchstart", "click", "dblclick"];
    keyboardEvents.forEach((type) => window.addEventListener(type, blockKeyboard, true));
    pointerEvents.forEach((type) => window.addEventListener(type, blockBackgroundPointer, { capture: true, passive: false }));
    window.addEventListener("focusin", keepFocus, true);
    dialogRef.current?.focus({ preventScroll: true });

    return () => {
      keyboardEvents.forEach((type) => window.removeEventListener(type, blockKeyboard, true));
      pointerEvents.forEach((type) => window.removeEventListener(type, blockBackgroundPointer, true));
      window.removeEventListener("focusin", keepFocus, true);
      background.forEach(([element, inert]) => {
        if (inert === null) element.removeAttribute("inert");
        else element.setAttribute("inert", inert);
      });
      if (overflow) document.body.style.setProperty("overflow", overflow, overflowPriority);
      else document.body.style.removeProperty("overflow");
      if (previousFocus?.isConnected) previousFocus.focus?.({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <Overlay ref={overlayRef} onClick={(event) => event.stopPropagation()}>
      <Dialog ref={dialogRef} role="dialog" aria-modal="true" aria-label="正在匯出 Word"
        aria-describedby={descriptionId} aria-busy="true" tabIndex={-1}>
        <WordIcon aria-hidden="true"><FaFileWord /><Spinner /></WordIcon>
        <h2>正在匯出 Word</h2>
        {displayFilename && <Filename title={displayFilename}>{displayFilename}</Filename>}
        <div id={descriptionId} role="status" aria-live="polite">
          <Message>{statusMessage}</Message>
          {showCount && <QuestionCount>題目進度 {current} / {total} 題</QuestionCount>}
          <Hint>{cancelling ? "停止後會關閉此視窗" : "完成後會自動下載"}</Hint>
        </div>
        {onCancel && <CancelButton ref={cancelButtonRef} type="button" aria-label="取消匯出" disabled={cancelling}
          onClick={requestCancel}>{cancelling ? "取消中…" : "取消匯出"}</CancelButton>}
      </Dialog>
    </Overlay>,
    document.body,
  );
}

const spin = keyframes`to { transform: rotate(360deg); }`;
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  box-sizing: border-box; padding: 20px; overflow-y: auto;
  background: rgb(8 15 25 / 72%); backdrop-filter: blur(5px);
  cursor: wait;
  @media (max-width: 480px) { padding: 16px; }
`;
const Dialog = styled.section`
  box-sizing: border-box; width: min(100%, 440px); max-height: calc(100vh - 40px);
  max-height: calc(100dvh - 40px); overflow-y: auto; padding: 36px 28px;
  border: 1px solid var(--app-border, #d7dee4); border-radius: 22px;
  background: var(--app-surface, #fff); color: var(--app-text, #27313c);
  box-shadow: 0 24px 90px #0006; text-align: center; outline: none;
  h2 { margin: 22px 0 10px; font-size: 23px; line-height: 1.4; }
  @media (max-width: 480px) {
    max-height: calc(100dvh - 32px); padding: 28px 20px;
    h2 { font-size: 21px; }
  }
`;
const WordIcon = styled.div`
  position: relative; display: grid; place-items: center;
  width: 82px; height: 82px; margin: 0 auto; border-radius: 22px;
  color: #2563b8; background: var(--app-surface-soft, #edf3fc);
  > svg:first-child { width: 43px; height: 43px; }
`;
const Spinner = styled(FiLoader)`
  position: absolute; right: -10px; bottom: -7px;
  width: 28px; height: 28px; box-sizing: content-box; padding: 5px;
  border: 2px solid var(--app-surface, #fff); border-radius: 50%;
  background: var(--app-surface-soft, #edf3fc); color: var(--app-icon-accent, #2563b8);
  animation: ${spin} 1s linear infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Filename = styled.p`
  margin: 0 0 20px; overflow-wrap: anywhere; line-height: 1.5;
  font-weight: 600; font-size: 14px;
`;
const Message = styled.p`
  margin: 0; color: var(--app-muted-text, #687380); font-size: 14px;
  line-height: 1.7; overflow-wrap: anywhere;
`;
const Hint = styled.p`
  margin: 14px 0 0; font-size: 13px; color: var(--app-muted-text, #687380);
`;

const QuestionCount = styled.p`
  margin: 16px 0 0; color: var(--app-text, #27313c); font-size: 19px; font-weight: 700; font-variant-numeric: tabular-nums;
`;
const CancelButton = styled.button`
  min-height: 44px; margin-top: 22px; padding: 10px 24px; border: 1px solid var(--app-border, #d7dee4); border-radius: 10px;
  color: var(--app-text, #27313c); background: var(--app-surface-soft, #edf3fc); font: inherit; cursor: pointer;
  &:focus-visible { outline: 2px solid var(--app-icon-accent, #2563b8); outline-offset: 3px; }
  &:hover:not(:disabled) { filter: brightness(.95); }
  &:disabled { opacity: .65; cursor: wait; }
`;
